import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { autoCurtailmentManagementService } from '@/services/eos/auto-curtailment-management.service'
import type { AutoCurtailmentFormState } from '@/types/eos/AutoCurtailmentManagement'
import type { ComputedRef } from 'vue'
import { NotifyFail, NotifySuccess } from '@/helpers/core/notifications'

export const useAutoCurtailmentManagement = () => {
  const queryClient = useQueryClient()

  const getRealTimeCurtailmentDecisionAttributesQuery = (siteId: ComputedRef<string | undefined>) =>
    useQuery({
      queryKey: ['real-time-curtailment-decision-attributes', siteId],
      queryFn: async () => {
        try {
          return await autoCurtailmentManagementService.getRealTimeCurtailmentDecisionAttributes(siteId.value!)
        } catch (error: any) {
          // If we get a 404, return null to indicate no attributes exist
          if (error?.response?.status === 404) {
            return null
          }
          throw error
        }
      },
      enabled: () => !!siteId.value,
    })

  const createRealTimeCurtailmentDecisionAttributesMutation = useMutation({
    mutationFn: ({ siteId, form }: { siteId: string; form: AutoCurtailmentFormState }) =>
      autoCurtailmentManagementService.createRealTimeCurtailmentDecisionAttributes(siteId, form),
    onSuccess: () => {
      NotifySuccess('Real Time Curtailment Decision Attributes created successfully')
      queryClient.invalidateQueries({ queryKey: ['real-time-curtailment-decision-attributes'] })
    },
    onError: (error: any) => {
      NotifyFail(error.message)
    },
  })

  const updateRealTimeCurtailmentDecisionAttributesMutation = useMutation({
    mutationFn: ({ siteId, form }: { siteId: string; form: AutoCurtailmentFormState }) =>
      autoCurtailmentManagementService.updateRealTimeCurtailmentDecisionAttributes(siteId, form),
    onSuccess: () => {
      NotifySuccess('Real Time Curtailment Decision Attributes updated successfully')
      queryClient.invalidateQueries({ queryKey: ['real-time-curtailment-decision-attributes'] })
    },
    onError: (error: any) => {
      NotifyFail(error.message)
    },
  })

  const deleteRealTimeCurtailmentDecisionAttributesMutation = useMutation({
    mutationFn: (siteId: string) =>
      autoCurtailmentManagementService.deleteRealTimeCurtailmentDecisionAttributes(siteId),
    onSuccess: (_, siteId) => {
      NotifySuccess('Real Time Curtailment Decision Attributes deleted successfully')
      // Immediately set the cache to null for this site
      queryClient.setQueryData(['real-time-curtailment-decision-attributes', siteId], null)
    },
    onError: (error: any) => {
      NotifyFail(error.message)
    },
  })

  return {
    getRealTimeCurtailmentDecisionAttributesQuery,
    createRealTimeCurtailmentDecisionAttributesMutation,
    updateRealTimeCurtailmentDecisionAttributesMutation,
    deleteRealTimeCurtailmentDecisionAttributesMutation,
  }
}
